import { AfterViewInit, Component, Input, Output, OnChanges, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { CrudPatientFacade, CrudPatientMode, CrudPatientUiState, DEFAULT_CRUD_PATIENT_MODE } from './crud-patient.facade';
import { FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { TITLES } from '../sign-up/sign-up.component';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { AccordionModule } from 'primeng/accordion';
import { EventEmitter } from '@angular/core';
import { EthnicityType, SexType } from '../../global-data-models/patient.model';
import { ActivatedRoute } from '@angular/router';
import { GlobalPhoneInputComponent } from '../../global-components/global-phone-input/global-phone-input.component';
import { LocaleService } from '../../global-services/locale.service';
import { DialogModule } from 'primeng/dialog';
import { PatientConsentComponent } from '../patient-consent/patient-consent.component';

export interface CalendarOptions {
  maxDate: Date;
  yearRange: string;
}

@Component({
  selector: 'app-crud-patient',
  imports: [...COMMON_IMPORTS, DropdownModule, CalendarModule, AccordionModule, GlobalPhoneInputComponent, DialogModule, PatientConsentComponent],
  templateUrl: './crud-patient.component.html',
})
export class CrudPatientComponent implements OnInit, OnChanges, OnDestroy {
  public readonly CrudPatientMode = CrudPatientMode;

  @Input() crudMode: CrudPatientMode = DEFAULT_CRUD_PATIENT_MODE;
  @Input() patientId?: string;
  @Input() orgId!: string;
  @Input() collapsible: boolean = false;

  public accordionActiveIndex: number | null = null;
  public showConsentDialog: boolean = false;
  public consentGiven: boolean = false;

  private onSubmitEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSubmit: Observable<void> = this.onSubmitEvent.asObservable();

  public readonly titles = ["Miss","Master","Ms","Mr","Mrs"];
  public ethnicities: EthnicityType[] = [];
  public sexes: SexType[] = [];
  public form: FormGroup;
  public parentalMyopiaOptions: string[] = ['One', 'Both', 'None'];

  private sub: Subscription = new Subscription();
  private submitSub = new Subscription();

  /* calendar specific data */
  public calendarOptions: CalendarOptions = {
    maxDate: new Date(),
    yearRange: String(new Date().getFullYear() - 150) + ':' + String(new Date().getFullYear()),
  };
  public constructor(
    private readonly facade: CrudPatientFacade,
    private readonly route: ActivatedRoute,
    private readonly localeService: LocaleService,
  ) {
    this.form = this.facade.uiState.form;
  }

  public ngOnInit(): void {
    this.facade.begin(this.orgId, this.crudMode, this.patientId);
    this.sub.add(
      this.facade.uiState$.subscribe((state: CrudPatientUiState) => {
        this.form = state.form;
        this.sexes = state.sexes;
        this.ethnicities = state.ethnicities;
        this.consentGiven = state.hasConsent ?? false;
      }),
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['crudMode'] && !changes['crudMode'].firstChange) || (changes['patientId'] && !changes['patientId'].firstChange)) {
      this.facade.begin(this.orgId, this.crudMode, this.patientId);
    }
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.submitSub.unsubscribe();
  }

  public async onFormSubmit(consentValue: string) {
    if (consentValue !== 'given' && consentValue !== 'not-given') {
      console.error(`Form submit failed as consentValue is unknown (${consentValue})`);
      return;
    }

    // Mark all fields as touched to show validation errors
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.consentGiven = consentValue === 'given' ? true : false;

    await this.facade.submit(this.consentGiven);

    this.showConsentDialog = false;

    this.onSubmitEvent.emit();
  }

  public getSelectedEthnicityName(): string {
    const ethnicityId = this.form.get('ethnicity_id')?.value;
    if (!ethnicityId) return '';
    const ethnicity = this.ethnicities.find((e) => e.id === ethnicityId);
    return ethnicity?.displayName || '';
  }

  public getSelectedSexName(): string {
    const sexId = this.form.get('sex_id')?.value;
    if (!sexId) return '';
    const sex = this.sexes.find((s) => s.id === sexId);
    return sex?.displayName || '';
  }

  public getPatientAge(): string {
    const dateOfBirth = this.form.get('dateOfBirth')?.value;
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  }

  public expandAccordion(): void {
    this.accordionActiveIndex = 0;
  }

  public collapseAccordion(): void {
    this.accordionActiveIndex = null;
  }

  public showConsentPopup(): void {
    this.showConsentDialog = true;
  }

  public onConsentDialogClose(): void {
    this.showConsentDialog = false;
  }
}
